import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Container, Card, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { obterEventoRsvp, salvarEventoRsvp } from '~/Services/Service';
import { ROUTE_EVENTOS_RSVP } from '~/constant';
import CorField from './CorField';

const extrairMensagemErro = (err, fallback) => {
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};

const validationSchema = Yup.object().shape({
  codigo_familia: Yup.object()
    .nullable()
    .required('Empreendimento é obrigatório'),
  nome_evento: Yup.string()
    .max(150, 'Nome do evento deve ter no máximo 150 caracteres')
    .required('Nome do evento é obrigatório'),
  foto_hero_url: Yup.string()
    .url('URL inválida. Deve começar com https://')
    .max(500, 'URL deve ter no máximo 500 caracteres')
    .required('URL da imagem hero é obrigatória'),
  texto_hero: Yup.string()
    .max(1000, 'Texto hero deve ter no máximo 1000 caracteres')
    .nullable(),
  texto_acao: Yup.string()
    .max(200, 'Texto de ação deve ter no máximo 200 caracteres')
    .nullable(),
  data_inicio_evento: Yup.date()
    .required('Data do evento é obrigatória')
    .typeError('Data do evento inválida'),
  data_fim_confirmacao: Yup.date()
    .required('Fim do período de confirmação é obrigatório')
    .typeError('Data inválida')
    .test(
      'nao-posterior-ao-evento',
      'Fim das confirmações deve ser anterior ou igual à data do evento',
      function (value) {
        const { data_inicio_evento } = this.parent;
        if (!data_inicio_evento || !value) return true;
        return new Date(value) <= new Date(data_inicio_evento);
      }
    ),
  max_acompanhantes: Yup.number()
    .integer('Máximo de acompanhantes deve ser número inteiro')
    .min(0, 'Mínimo de acompanhantes é 0')
    .max(10, 'Máximo de acompanhantes é 10')
    .required('Máximo de acompanhantes é obrigatório'),
  logo_url: Yup.string()
    .url('URL inválida. Deve começar com https://')
    .max(500, 'URL deve ter no máximo 500 caracteres')
    .nullable(),
  subtitulo_hero_linha1: Yup.string()
    .max(100, 'Subtítulo (linha 1) deve ter no máximo 100 caracteres')
    .nullable(),
  subtitulo_hero_linha2: Yup.string()
    .max(100, 'Subtítulo (linha 2) deve ter no máximo 100 caracteres')
    .nullable(),
  nome_destaque: Yup.string()
    .max(50, 'Nome em destaque deve ter no máximo 50 caracteres')
    .nullable(),
  cor_primaria: Yup.string()
    .matches(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida. Use o formato #RRGGBB')
    .nullable(),
  cor_secundaria: Yup.string()
    .matches(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida. Use o formato #RRGGBB')
    .nullable(),
});

const EventoFormPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const isEdit = !!id;
  const [loadingEvento, setLoadingEvento] = useState(false);
  const [initialValues, setInitialValues] = useState({
    codigo: null,
    codigo_familia: null,
    nome_evento: '',
    foto_hero_url: '',
    texto_hero: '',
    texto_acao: '',
    data_inicio_evento: '',
    data_fim_confirmacao: '',
    max_acompanhantes: 2,
    logo_url: '',
    subtitulo_hero_linha1: '',
    subtitulo_hero_linha2: '',
    nome_destaque: '',
    cor_primaria: '',
    cor_secundaria: '',
  });

  const empreendimentosRedux = useSelector(
    (state) => state.empreendimentos.list
  );
  const empreendimentoOptions = useMemo(
    () =>
      (empreendimentosRedux || []).map((e) => ({
        value: e.codigo,
        label: e.descricao,
      })),
    [empreendimentosRedux]
  );

  useEffect(() => {
    if (isEdit) {
      setLoadingEvento(true);
      obterEventoRsvp(id)
        .then((evento) => {
          const optEmpreendimento = empreendimentoOptions.find(
            (e) => e.value === evento.codigo_familia
          );
          setInitialValues({
            codigo: evento.codigo,
            codigo_familia: optEmpreendimento || null,
            nome_evento: evento.nome_evento,
            foto_hero_url: evento.foto_hero_url,
            texto_hero: evento.texto_hero || '',
            texto_acao: evento.texto_acao || '',
            data_inicio_evento: evento.data_inicio_evento
              ? evento.data_inicio_evento.substring(0, 10)
              : '',
            data_fim_confirmacao: evento.data_fim_confirmacao
              ? evento.data_fim_confirmacao.substring(0, 10)
              : '',
            max_acompanhantes: evento.max_acompanhantes,
            logo_url: evento.logo_url || '',
            subtitulo_hero_linha1: evento.subtitulo_hero_linha1 || '',
            subtitulo_hero_linha2: evento.subtitulo_hero_linha2 || '',
            nome_destaque: evento.nome_destaque || '',
            cor_primaria: evento.cor_primaria || '',
            cor_secundaria: evento.cor_secundaria || '',
          });
        })
        .catch((err) => {
          const msg = extrairMensagemErro(err, 'Erro ao carregar evento.');
          toast.error(msg);
          history.push(ROUTE_EVENTOS_RSVP);
        })
        .finally(() => setLoadingEvento(false));
    }
  }, [id, isEdit, history, empreendimentoOptions]);

  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      codigo: values.codigo,
      codigo_familia: values.codigo_familia ? values.codigo_familia.value : null,
      nome_evento: values.nome_evento,
      foto_hero_url: values.foto_hero_url,
      texto_hero: values.texto_hero || null,
      texto_acao: values.texto_acao || null,
      data_inicio_evento: `${values.data_inicio_evento}T00:00:00`,
      data_fim_confirmacao: `${values.data_fim_confirmacao}T23:59:59`,
      max_acompanhantes: parseInt(values.max_acompanhantes, 10),
      logo_url: values.logo_url || null,
      subtitulo_hero_linha1: values.subtitulo_hero_linha1 || null,
      subtitulo_hero_linha2: values.subtitulo_hero_linha2 || null,
      nome_destaque: values.nome_destaque || null,
      cor_primaria: values.cor_primaria || null,
      cor_secundaria: values.cor_secundaria || null,
    };

    salvarEventoRsvp(payload)
      .then((res) => {
        toast.success(res.mensagem || 'Evento salvo com sucesso.');
        history.push(ROUTE_EVENTOS_RSVP);
      })
      .catch((err) => {
        const msg = extrairMensagemErro(err, 'Erro ao salvar evento.');
        toast.error(msg);
      })
      .finally(() => setSubmitting(false));
  };

  const handleCancelar = () => {
    history.push(ROUTE_EVENTOS_RSVP);
  };

  if (loadingEvento) {
    return (
      <Container fluid>
        <Header
          titulo={isEdit ? 'Editar Evento' : 'Novo Evento'}
          subtitulo="Sistema RSVP Eventos"
        />
        <Card className="mt-3">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Carregando evento...</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Header
        titulo={isEdit ? 'Editar Evento' : 'Novo Evento'}
        subtitulo="Sistema RSVP Eventos"
      />

      <Card className="mt-3">
        <Card.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Empreendimento *</Form.Label>
                      <Select
                        placeholder="Selecione um empreendimento..."
                        options={empreendimentoOptions}
                        value={values.codigo_familia}
                        onChange={(opt) => setFieldValue('codigo_familia', opt)}
                        onBlur={() => setFieldTouched('codigo_familia', true)}
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        noOptionsMessage={() => 'Nenhum empreendimento encontrado'}
                        isClearable
                        className={
                          touched.codigo_familia && errors.codigo_familia
                            ? 'is-invalid'
                            : ''
                        }
                      />
                      {touched.codigo_familia && errors.codigo_familia && (
                        <div className="invalid-feedback d-block">
                          {errors.codigo_familia}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Nome do Evento *</Form.Label>
                      <Form.Control
                        type="text"
                        name="nome_evento"
                        value={values.nome_evento}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.nome_evento && !!errors.nome_evento}
                        placeholder="Ex: Liberação Setgarden"
                        maxLength={150}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.nome_evento}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Máximo de Acompanhantes *</Form.Label>
                      <Form.Control
                        type="number"
                        name="max_acompanhantes"
                        value={values.max_acompanhantes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.max_acompanhantes && !!errors.max_acompanhantes
                        }
                        min={0}
                        max={10}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.max_acompanhantes}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        De 0 a 10 acompanhantes por confirmação
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>URL da Imagem Hero *</Form.Label>
                      <Form.Control
                        type="url"
                        name="foto_hero_url"
                        value={values.foto_hero_url}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.foto_hero_url && !!errors.foto_hero_url}
                        placeholder="https://cdn.setpar.com.br/eventos/setgarden-hero.jpg"
                        maxLength={500}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.foto_hero_url}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        URL completa da imagem de fundo (hero) da landing page
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Texto Hero (opcional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="texto_hero"
                        value={values.texto_hero}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.texto_hero && !!errors.texto_hero}
                        placeholder="Você está convidado para a entrega do seu imóvel"
                        maxLength={1000}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.texto_hero}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Texto de Ação (opcional)</Form.Label>
                      <Form.Control
                        type="text"
                        name="texto_acao"
                        value={values.texto_acao}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.texto_acao && !!errors.texto_acao}
                        placeholder="Confirme sua presença"
                        maxLength={200}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.texto_acao}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Texto exibido no badge de chamada para ação
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Data do Evento *</Form.Label>
                      <Form.Control
                        type="date"
                        name="data_inicio_evento"
                        value={values.data_inicio_evento}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.data_inicio_evento && !!errors.data_inicio_evento
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.data_inicio_evento}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Dia em que a entrega acontece
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Fim das Confirmações *</Form.Label>
                      <Form.Control
                        type="date"
                        name="data_fim_confirmacao"
                        value={values.data_fim_confirmacao}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.data_fim_confirmacao &&
                          !!errors.data_fim_confirmacao
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.data_fim_confirmacao}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Quando a landing para de aceitar RSVP (início é a data de cadastro do evento)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />
                <h6 className="text-muted mb-3">
                  Identidade visual da landing (opcional)
                </h6>
                <Form.Text className="text-muted mb-3 d-block">
                  Campos opcionais — se deixados em branco, a landing usa os
                  valores padrão da Setpar (logo Setpar, subtítulo &quot;Evento
                  oficial / de liberação&quot;, sem nome em destaque). Preencha
                  para personalizar a página por empreendimento sem mexer no
                  fonte.
                </Form.Text>

                <Row>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>URL do logo do empreendimento</Form.Label>
                      <Form.Control
                        type="url"
                        name="logo_url"
                        value={values.logo_url}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.logo_url && !!errors.logo_url}
                        placeholder="https://repositorio.setcorp.com.br/imagens/empreendimentos/.../logo.png"
                        maxLength={500}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.logo_url}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Aparece no canto superior esquerdo da landing. Vazio = logo Setpar.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Subtítulo do hero — linha 1</Form.Label>
                      <Form.Control
                        type="text"
                        name="subtitulo_hero_linha1"
                        value={values.subtitulo_hero_linha1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.subtitulo_hero_linha1 &&
                          !!errors.subtitulo_hero_linha1
                        }
                        placeholder="Evento oficial"
                        maxLength={100}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.subtitulo_hero_linha1}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Subtítulo do hero — linha 2</Form.Label>
                      <Form.Control
                        type="text"
                        name="subtitulo_hero_linha2"
                        value={values.subtitulo_hero_linha2}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.subtitulo_hero_linha2 &&
                          !!errors.subtitulo_hero_linha2
                        }
                        placeholder="de liberação"
                        maxLength={100}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.subtitulo_hero_linha2}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        A linha 2 é renderizada com contorno (texto vazado).
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Nome em destaque</Form.Label>
                      <Form.Control
                        type="text"
                        name="nome_destaque"
                        value={values.nome_destaque}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.nome_destaque && !!errors.nome_destaque
                        }
                        placeholder="SETGARDEN"
                        maxLength={50}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.nome_destaque}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Texto grande na caixa lateral do hero. Vazio = caixa não aparece.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <CorField
                      name="cor_primaria"
                      label="Cor primária do degradê"
                      ajuda="Degradê da cor para transparente. Vazio = cor padrão Setpar."
                      value={values.cor_primaria}
                      error={errors.cor_primaria}
                      touched={touched.cor_primaria}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />
                  </Col>

                  <Col md={6}>
                    <CorField
                      name="cor_secundaria"
                      label="Cor secundária do degradê"
                      ajuda="Degradê da cor para transparente. Vazio = cor padrão Setpar."
                      value={values.cor_secundaria}
                      error={errors.cor_secundaria}
                      touched={touched.cor_secundaria}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />
                  </Col>
                </Row>

                <div className="mt-4">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                    className="mr-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="mr-1"
                        />
                        Salvando...
                      </>
                    ) : (
                      'Salvar'
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelar}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EventoFormPage;
